import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Search, Plus, Loader2, Wrench, Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SPECIALTIES = {
  excavation: 'Excavation',
  electricite: 'Electricite',
  plomberie: 'Plomberie',
  beton: 'Beton',
  paysagement: 'Paysagement',
  autre: 'Autre',
};

const STATUSES = {
  actif: { label: 'Actif', cls: 'bg-green-500/10 text-green-600' },
  inactif: { label: 'Inactif', cls: 'bg-gray-500/10 text-gray-500' },
};

export function SubcontractorsListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', specialty: 'excavation', status: 'actif', notes: '' });

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/subcontractors', { params: { search } });
      setSubs(data.subcontractors);
      setTotal(data.total);
    } catch { toast.error(tf('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const resetForm = () => setForm({ first_name: '', last_name: '', phone: '', email: '', specialty: 'excavation', status: 'actif', notes: '' });

  const handleAdd = async () => {
    if (!form.first_name && !form.last_name) {
      toast.error(tf('Prenom ou nom requis', 'First or last name required'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/subcontractors', form);
      toast.success(tf('Sous-traitant ajoute', 'Subcontractor added'));
      setShowAdd(false);
      resetForm();
      loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || tf('Erreur', 'Error')); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="subcontractors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">
            {tf('Sous-traitants', 'Subcontractors')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {total} {tf('sous-traitant' + (total !== 1 ? 's' : ''), 'subcontractor' + (total !== 1 ? 's' : ''))}
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-subcontractor-btn">
          <Plus className="h-4 w-4 mr-2" />
          {tf('Ajouter un sous-traitant', 'Add subcontractor')}
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={tf('Rechercher par nom, specialite, telephone...', 'Search by name, specialty, phone...')}
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10" data-testid="subcontractors-search"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : subs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Wrench className="h-12 w-12 mb-4 opacity-40" />
              <p className="font-medium">{tf('Aucun sous-traitant', 'No subcontractors')}</p>
              <p className="text-sm">{tf('Ajoutez votre premier sous-traitant', 'Add your first subcontractor')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="subcontractors-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Prenom', 'First name')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Nom', 'Last name')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Telephone', 'Phone')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Specialite', 'Specialty')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Statut', 'Status')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Date d\'ajout', 'Date added')}</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(sub => (
                    <tr
                      key={sub.subcontractor_id}
                      className="border-b hover:bg-muted/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/subcontractors/${sub.subcontractor_id}`)}
                      data-testid={`sub-row-${sub.subcontractor_id}`}
                    >
                      <td className="p-3 text-sm">{sub.first_name || '\u2014'}</td>
                      <td className="p-3 font-medium text-sm">{sub.last_name || '\u2014'}</td>
                      <td className="p-3 text-sm">
                        {sub.phone ? (<span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{sub.phone}</span>) : '\u2014'}
                      </td>
                      <td className="p-3 text-sm">{SPECIALTIES[sub.specialty] || sub.specialty || '\u2014'}</td>
                      <td className="p-3 text-sm">
                        <Badge className={STATUSES[sub.status]?.cls || 'bg-gray-100'}>
                          {STATUSES[sub.status]?.label || sub.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(sub.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {tf('Ajouter un sous-traitant', 'Add subcontractor')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{tf('Prenom', 'First name')}</Label>
                <Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} placeholder="Jean" data-testid="sub-firstname" />
              </div>
              <div className="space-y-1.5">
                <Label>{tf('Nom', 'Last name')}</Label>
                <Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} placeholder="Lavoie" data-testid="sub-lastname" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{tf('Telephone', 'Phone')}</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="514-555-1234" data-testid="sub-phone" />
              </div>
              <div className="space-y-1.5">
                <Label>Courriel</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="jean@exemple.com" data-testid="sub-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{tf('Specialite', 'Specialty')}</Label>
                <Select value={form.specialty} onValueChange={v => setForm(f => ({...f, specialty: v}))}>
                  <SelectTrigger data-testid="sub-specialty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excavation">Excavation</SelectItem>
                    <SelectItem value="electricite">{tf('Electricite', 'Electrical')}</SelectItem>
                    <SelectItem value="plomberie">{tf('Plomberie', 'Plumbing')}</SelectItem>
                    <SelectItem value="beton">{tf('Beton', 'Concrete')}</SelectItem>
                    <SelectItem value="paysagement">{tf('Paysagement', 'Landscaping')}</SelectItem>
                    <SelectItem value="autre">{tf('Autre', 'Other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{tf('Statut', 'Status')}</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="sub-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">{tf('Actif', 'Active')}</SelectItem>
                    <SelectItem value="inactif">{tf('Inactif', 'Inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3} placeholder="Notes..." data-testid="sub-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); resetForm(); }}>{tf('Annuler', 'Cancel')}</Button>
            <Button onClick={handleAdd} disabled={saving || (!form.first_name && !form.last_name)} data-testid="save-subcontractor-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tf('Ajouter', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
